import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", phone: "", email: "", message: "" };

const inputCls =
  "w-full h-12 md:h-14 border border-slate-300 rounded-md px-4 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 bg-white transition-colors";

const Contact = () => {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/quotes`, form);
      setStatus("sent");
      setForm(empty);
      toast.success("Thanks — your request has been sent. We'll get back to you shortly.");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong sending your request. Please call us instead.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#FAFAFA] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16 items-start">
        <Reveal>
          <Overline>Get in touch</Overline>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Need an Electrician?
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-slate-600 max-w-lg">
            Tell us what you need and we'll get back to you. For anything
            urgent, calling is usually fastest.
          </p>

          <div className="mt-10 space-y-4">
            <a
              data-testid="contact-phone-link"
              href={PHONE_TEL}
              className="flex items-center gap-4 bg-white border border-slate-200 rounded-md p-5 hover:border-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <span className="w-11 h-11 shrink-0 bg-blue-600 rounded-md flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Call us</span>
                <span className="block font-heading text-xl font-bold text-slate-900">{PHONE_DISPLAY}</span>
              </span>
            </a>
            <a
              data-testid="contact-email-link"
              href={`mailto:${EMAIL}`}
              className="flex items-center gap-4 bg-white border border-slate-200 rounded-md p-5 hover:border-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              <span className="w-11 h-11 shrink-0 bg-amber-400 rounded-md flex items-center justify-center">
                <Mail className="w-5 h-5 text-slate-900" />
              </span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</span>
                <span className="block font-semibold text-slate-900 break-all">{EMAIL}</span>
              </span>
            </a>
            <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-md p-5">
              <span className="w-11 h-11 shrink-0 bg-slate-900 rounded-md flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Opening hours</span>
                <span className="block font-semibold text-slate-900">Mon–Fri 8am–6pm · Sat 9am–1pm</span>
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="quote-form"
            onSubmit={submit}
            className="bg-white border border-slate-200 rounded-md p-7 md:p-10"
          >
            <h3 className="font-heading text-2xl font-bold text-slate-900">Request a quote</h3>
            <p className="mt-2 text-sm text-slate-600">
              A few details and we'll come back to you — usually the same working day.
            </p>

            <div className="mt-8 space-y-5">
              <div>
                <label htmlFor="quote-name" className="block text-sm font-bold text-slate-900 mb-2">Name</label>
                <input id="quote-name" data-testid="quote-form-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="quote-phone" className="block text-sm font-bold text-slate-900 mb-2">Phone</label>
                  <input id="quote-phone" data-testid="quote-form-phone-input" type="tel" required value={form.phone} onChange={set("phone")} placeholder="07..." className={inputCls} />
                </div>
                <div>
                  <label htmlFor="quote-email" className="block text-sm font-bold text-slate-900 mb-2">Email</label>
                  <input id="quote-email" data-testid="quote-form-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                </div>
              </div>
              <div>
                <label htmlFor="quote-message" className="block text-sm font-bold text-slate-900 mb-2">Message</label>
                <textarea id="quote-message" data-testid="quote-form-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us what you need — e.g. extra sockets in the kitchen, an EICR for a rental property..." className={`${inputCls} h-auto py-3.5 resize-y`} />
              </div>

              {status === "sent" && (
                <p data-testid="quote-form-success" className="flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-md px-4 py-3">
                  <CheckCircle2 className="w-4 h-4" /> Request sent — we'll be in touch shortly.
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  data-testid="quote-form-submit-button"
                  type="submit"
                  disabled={status === "sending"}
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold px-8 py-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                >
                  {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {status === "sending" ? "Sending..." : "Request a Quote"}
                </button>
                <a
                  data-testid="quote-form-call-button"
                  href={PHONE_TEL}
                  className="inline-flex items-center justify-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-900 font-bold px-8 py-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                >
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </div>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
